// suit.js — procedural box-humanoid player avatar, ported from doc/suits.html (createVoxelCharacter +
// applyBlendedPose). No GLTF / skeleton / AnimationMixer: the figure is a THREE.Group of boxes with 4 limb
// sub-groups, animated by a lightweight weight-blend "pose" engine (idle ↔ walk ↔ run). Used to render remote
// players (peers). Keep this dependency-light: only THREE core.
import * as THREE from '/lib/three/three.module.min.js';   // REAL path, not the bare "three" specifier (import maps need Safari 16.4+; this must resolve on Safari 15.6)

// Figure metrics in LOCAL units (before scaling). Origin is the torso centre; the feet plane is SUIT_LOCAL_FEET
// BELOW it and the head top is ~+1.4, so the whole figure is ~SUIT_LOCAL_HEIGHT tall. Callers place the anchor at
// the feet and set group.userData.restY = SUIT_LOCAL_FEET so the feet sit on the surface (see the renderer).
export const SUIT_LOCAL_HEIGHT = 2.95;
export const SUIT_LOCAL_FEET = 1.55;   // measured boot-bottom plane below the torso origin (lift the suit by this so feet touch the surface)
// The glove box's centre in ARM-LOCAL space (see the arm boxes below). Exported so callers can anchor an effect to
// the hand by transforming it through limbs.rightArm — which tracks the arm as it swings or waves, unlike a fixed
// offset from the suit root. limbs.rightArm really is the figure's own right arm; see the note in buildSuit.
export const SUIT_HAND_LOCAL_Y = -0.8;

// Build one avatar. colors: { base, secondary, accent } (hex ints). Returns a THREE.Group with `.limbs`
// {leftArm,rightArm,leftLeg,rightLeg} and a `.dispose()` that frees its geometry + materials.
export function buildSuit(colors = {}, opts = {}) {
  const data = { base: colors.base ?? 0xffffff, secondary: colors.secondary ?? 0x808080, accent: colors.accent ?? 0xcd853f };
  const cast = opts.castShadow !== false, receive = opts.receiveShadow === true;   // cast by default; receiving is opt-in (the on-world shadow pass is separate/expensive)
  const group = new THREE.Group();
  const limbs = { leftArm: new THREE.Group(), rightArm: new THREE.Group(), leftLeg: new THREE.Group(), rightLeg: new THREE.Group() };
  group.add(limbs.leftArm, limbs.rightArm, limbs.leftLeg, limbs.rightLeg);
  // LEFT/RIGHT HERE ARE THE FIGURE'S OWN, not the viewer's — and that is why rightArm sits at NEGATIVE x.
  // The figure faces its local +Z (that is where the visor box goes, and the renderer maps local +Z to the heading;
  // see makeBasis in system-view.js) with +Y up. three.js's convention is that an observer looking down -Z has +X on
  // their right, so an observer facing the other way (+Z) has their right at -X. Hence own-right = -X.
  // These were previously mirrored, which put the peer mining laser and the wave emote on the wrong arm.
  limbs.rightArm.position.set(-0.6, 0.4, 0);
  limbs.leftArm.position.set(0.6, 0.4, 0);

  // Three shared materials per suit (base / secondary / accent) instead of one per box — ~15 boxes → 3 materials.
  // The 'secondary'/'accent' materials are used ONLY for detail parts (bands, visor, boots) that sit flush ON the base
  // body; 'base' only for the body itself. Detail parts get polygonOffset (pulled toward the camera) so they win the
  // depth test against the coplanar base surface under them — otherwise they z-FIGHT and flicker. The on-world camera's
  // huge near:far ratio (~0.0012 : 5e6) wrecks depth precision, so this needs a firmer offset (units −8) than the
  // reference doc/suits.html (factor −1 only, at a benign 0.1 : 100 range); the small geometry swell (+SWELL below)
  // helps at grazing angles too. (Ported from createVoxelCharacter's isDetail path.)
  // opts.makeMaterial(colorHex, isDetail, isVisor) lets the caller supply its own material — the system view passes
  // voxel-lit ones so peers receive the SAME star lighting + ray-marched terrain shadows as the ground they stand on.
  // The VISOR is special: it GLOWS — an unlit/full-bright material, unaffected by lights, the ray-march shadow or eclipse
  // dimming — so callers branch on isVisor. It keeps the detail polygonOffset so it doesn't z-fight the head beneath it.
  const mkMat = typeof opts.makeMaterial === 'function' ? opts.makeMaterial
    : (color, detail, visor) => visor
        ? new THREE.MeshBasicMaterial({ color, polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -8 })
        : new THREE.MeshStandardMaterial({ color, roughness: 1, polygonOffset: detail, polygonOffsetFactor: detail ? -1 : 0, polygonOffsetUnits: detail ? -8 : 0 });
  const mats = { base: mkMat(data.base, false, false), secondary: mkMat(data.secondary, true, false), accent: mkMat(data.accent, true, true) };
  const SWELL = 0.004;   // detail boxes grow a hair so their faces clear the base body's coplanar faces
  const geos = [];
  // EVERY BOX SAYS WHAT IT IS (mesh.userData.part: torso, head, crown, visor, arm, leg, boot), for a caller that has to
  // find a part of the figure rather than draw all of it: the Profile portrait frames the posed head and shoulders
  // from these (avatar-portrait.mjs suitBustParts) and finds the visor to give it its glow. Set by `part` below as
  // each group of boxes is built; nothing on the world reads it.
  let part = 'torso';
  const box = (w, h, d, x, y, z, matKey, parent = group) => {
    const s = matKey === 'base' ? 0 : SWELL;
    const geo = new THREE.BoxGeometry(w + s, h + s, d + s); geos.push(geo);
    const mesh = new THREE.Mesh(geo, mats[matKey]);
    mesh.position.set(x, y, z); mesh.castShadow = cast; mesh.receiveShadow = receive;
    mesh.userData.part = part;
    parent.add(mesh);
  };
  // torso
  box(0.8, 1.2, 0.5, 0, 0, 0, 'base');
  box(0.8, 0.2, 0.5, 0, 0.3, 0, 'secondary');
  box(0.65, 0.9, 0.35, 0, 0.1, -0.4, 'secondary');
  // head + visor
  part = 'head';  box(0.7, 0.7, 0.7, 0, 1.05, 0, 'base');
  part = 'crown'; box(0.7, 0.15, 0.7, 0, 1.33, 0, 'secondary');
  part = 'visor'; box(0.5, 0.4, 0.1, 0, 0.98, 0.4, 'accent');
  // arms
  part = 'arm';
  [limbs.leftArm, limbs.rightArm].forEach(arm => {
    box(0.3, 0.8, 0.3, 0, -0.4, 0, 'base', arm);
    box(0.3, 0.15, 0.3, 0, -0.3, 0, 'secondary', arm);
    box(0.3, 0.2, 0.3, 0, -0.8, 0, 'secondary', arm);
  });
  // legs (+ boots)
  part = 'leg';  box(0.35, 1.0, 0.35, 0.22, -1.0, 0, 'base', limbs.leftLeg);
                 box(0.4, 0.2, 0.4, 0.22, -0.8, 0, 'secondary', limbs.leftLeg);
  part = 'boot'; box(0.4, 0.3, 0.45, 0.22, -1.4, 0.05, 'secondary', limbs.leftLeg);
  part = 'leg';  box(0.35, 1.0, 0.35, -0.22, -1.0, 0, 'base', limbs.rightLeg);
                 box(0.4, 0.2, 0.4, -0.22, -0.8, 0, 'secondary', limbs.rightLeg);
  part = 'boot'; box(0.4, 0.3, 0.45, -0.22, -1.4, 0.05, 'secondary', limbs.rightLeg);

  group.limbs = limbs;
  group.userData.restY = 0;   // renderer sets this to SUIT_LOCAL_FEET on-world (feet on the surface); 0 = float around the torso origin
  group.dispose = () => { geos.forEach(g => g.dispose()); Object.values(mats).forEach(m => m.dispose()); };
  return group;
}

// Per-avatar animation state. Blend weights lerp toward targets; phase drives the walk cycle.
export function makeSuitAnim() { return { weights: { walking: 0, running: 0, jumping: 0, crouching: 0, waving: 0, mining: 0 }, targets: { walking: 0, running: 0, jumping: 0, crouching: 0, waving: 0, mining: 0 }, transitionRate: 10, phase: 0, waveT: 0, idleT: 0 }; }

// Advance + apply the pose. dt seconds; state = { walking, running, jumping, crouching }. walking/running/jumping are
// mutually exclusive (the sender's motion is one of idle/walk/run/jump), but `crouching` is a MODIFIER that rides on top
// of idle or walk — holding the hunch key while moving is a sneak, so the leg swing has to survive the crouch. Sets the
// 4 limbs' rotations and a small body bob/lean, all LOCAL to `group`, so an anchoring parent owns world
// position/orientation/scale. Idle = all weights → 0 = the neutral built pose.
export function poseSuit(group, a, dt, state) {
  if (!group || !group.limbs) return;
  a.targets.walking = state && state.walking && !state.running ? 1 : 0;
  a.targets.running = state && state.running ? 1 : 0;
  a.targets.jumping = state && state.jumping ? 1 : 0;
  a.targets.crouching = state && state.crouching ? 1 : 0;
  a.targets.waving = state && state.waving ? 1 : 0;
  a.targets.mining = state && state.mining ? 1 : 0;
  if (a.weights.mining == null) { a.weights.mining = 0; a.idleT = a.idleT || 0; }   // (an anim made before mining was a pose)
  const lerp = 1 - Math.exp(-a.transitionRate * Math.max(0, dt));
  a.weights.mining += (a.targets.mining - a.weights.mining) * lerp;
  a.idleT += Math.max(0, dt);
  a.weights.walking += (a.targets.walking - a.weights.walking) * lerp;
  a.weights.running += (a.targets.running - a.weights.running) * lerp;
  a.weights.jumping += (a.targets.jumping - a.weights.jumping) * lerp;
  a.weights.crouching += (a.targets.crouching - a.weights.crouching) * lerp;
  a.weights.waving += (a.targets.waving - a.weights.waving) * lerp;

  const loco = a.weights.walking + a.weights.running;
  const speed = (a.weights.walking * 20 + a.weights.running * 32) / (loco || 1);
  if (loco > 0.01) a.phase += 0.36 * speed * dt;
  const t = a.phase;

  let bodyY = 0, bodyX = 0, bodyZ = 0, lLegX = 0, rLegX = 0, lArmX = 0, rArmX = 0, lArmZ = 0, rArmZ = 0;
  if (loco > 0.01) {
    const swing = Math.sin(t) * 0.6;
    const bob = Math.pow(Math.sin(t * 0.5), 2) * (0.08 + a.weights.running * 0.1);
    lLegX -= swing * loco; rLegX += swing * loco;
    lArmX += swing * 0.8 * loco; rArmX -= swing * 0.8 * loco;
    lArmZ -= Math.abs(swing) * 0.1 * loco; rArmZ += Math.abs(swing) * 0.1 * loco;   // sign follows the arm's side: leftArm is now at +x
    bodyY += bob * loco;
    bodyX += (0.1 + a.weights.running * 0.15) * loco;
    bodyZ -= Math.sin(t) * 0.05 * loco;   // ROLL about the forward axis — chirality-carrying like the arm-Z splays,
                                          // so it flips with them. Left unflipped, the torso would roll AGAINST the
                                          // stride instead of with it, since the limb swap mirrors the gait.
  }
  // HUNCH (hold shift / the CROUCH button): torso pitched forward, legs compressed, arms drawn in, body dropped.
  // The squat comes from SHORTENING the legs, not from rotating them: these legs are rigid boxes pivoting at the hip
  // with no knee, so rotating them forward tips the boot's toe DOWN — the deep 0.45 boot cancels most of the lift and
  // the feet end up buried (measured: 0.13 under). Scaling the leg group instead keeps the boots flat and level, so the
  // drop is exactly FEET·COMPRESS and the feet stay planted (the float/sink bug this avatar has been through twice).
  // The legs also counter-rotate out of the body's lean, keeping them world-vertical under the hunched torso.
  // Offsets are ADDED, not blended, so a crouch-walk keeps its leg swing.
  const cw = a.weights.crouching, LEAN = 0.55, COMPRESS = 0.18;
  const legScale = 1 - COMPRESS * cw;
  if (cw > 0.01) {
    lLegX += -LEAN * cw; rLegX += -LEAN * cw;                         // cancel the body pitch → legs stay under the player
    lArmX += -LEAN * 0.85 * cw; rArmX += -LEAN * 0.85 * cw;           // arms hang ~world-vertical instead of jutting out front
    lArmZ += -0.12 * cw; rArmZ += 0.12 * cw;                          // elbows drawn in (toward the centreline)
    bodyX = bodyX * (1 - cw) + LEAN * cw;                             // replaces the walk lean rather than stacking on it
    bodyY -= SUIT_LOCAL_FEET * COMPRESS * cw;                         // == exactly how much the shortened legs raise the boots
  }
  // WAVE (H): right arm up and rocking. Numbers lifted from doc/suits.html's waving pose so the emote reads the
  // same here as in the reference — arm swung overhead (~-2.79 rad) with a small cosine rock. Blended, not assigned,
  // so a wave while idle looks right and a wave that overlaps the walk swing still resolves to something sane.
  // Uses its own clock (a.waveT) rather than the locomotion phase, which only advances while moving.
  const vw = a.weights.waving;
  if (vw > 0.01) {
    a.waveT += Math.max(0, dt);
    rArmZ = rArmZ * (1 - vw) + (-Math.cos(a.waveT * 12) * 0.1745 - 2.7925) * vw;   // the RIGHT arm (now at -0.6) waves, matching doc/suits.html:848 verbatim — no mirror needed once the sides are truthful
    rArmX = rArmX * (1 - vw);                                                            // no fore/aft swing on the waving arm
  } else a.waveT = 0;
  const jw = a.weights.jumping;   // JUMP: legs tucked (knees forward/up), arms thrown overhead — blends over the walk/idle/crouch pose
  if (jw > 0.01) {
    lLegX = lLegX * (1 - jw) + (-0.5) * jw; rLegX = rLegX * (1 - jw) + (-0.7) * jw;   // slight split tuck
    lArmX = lArmX * (1 - jw) + (-2.6) * jw; rArmX = rArmX * (1 - jw) + (-2.6) * jw;   // arms up
    lArmZ = lArmZ * (1 - jw) + (-0.25) * jw; rArmZ = rArmZ * (1 - jw) + (0.25) * jw;
    bodyX = bodyX * (1 - jw); bodyY = bodyY * (1 - jw);   // no walk lean/bob mid-air
  }

  // IDLE AND MINING (the owner's play review, Oct 23, v10: "a walk cycle, an idle sway and a mining pose for every figure").
  // IDLE (state.idle, asked for by a caller that animates over time; a still portrait leaves it off): standing, the figure
  // breathes (a slow rise and fall), its arms hang a little out and swing with the breath, and it sways side to side, all
  // faded out as it moves, jumps, waves or mines. MINING (state.mining, with state.pitch the look's): the right arm points
  // where the beam goes, trembling a little with the cut, the left braced forward, the body leaning in.
  const mw = a.weights.mining, busy = Math.min(1, loco + jw + vw + mw), iw = state && state.idle ? 1 - busy : 0, it = a.idleT;
  if (iw > 0.01) {
    const br = Math.sin(it * 1.9);
    bodyY += 0.022 * br * iw;
    bodyZ += 0.03 * Math.sin(it * 0.7) * iw;
    lArmZ += (0.05 + 0.03 * br) * iw; rArmZ -= (0.05 + 0.03 * br) * iw;
    lArmX += 0.04 * Math.sin(it * 0.7 + 1.1) * iw; rArmX += 0.04 * Math.sin(it * 0.7 + 2.3) * iw;
  }
  if (mw > 0.01) {
    const pitch = Math.max(-0.9, Math.min(0.9, (state && state.pitch) || 0));
    rArmX = rArmX * (1 - mw) + (-1.45 - pitch + 0.035 * Math.sin(it * 31)) * mw;
    rArmZ = rArmZ * (1 - mw);
    lArmX = lArmX * (1 - mw) + (-0.45) * mw;
    bodyX += 0.08 * mw;
  }
  group.position.y = (group.userData.restY || 0) + bodyY;   // rest lift (feet on surface) + walk bob
  group.rotation.x = bodyX; group.rotation.z = bodyZ;       // forward lean while moving (Y is owned by the anchor)
  const L = group.limbs;
  L.leftLeg.rotation.x = lLegX; L.rightLeg.rotation.x = rLegX;
  L.leftLeg.scale.y = L.rightLeg.scale.y = legScale;   // set unconditionally so releasing the hunch restores full-length legs
  L.leftArm.rotation.x = lArmX; L.rightArm.rotation.x = rArmX;
  L.leftArm.rotation.z = lArmZ; L.rightArm.rotation.z = rArmZ;
}
